/*
  La Matcha — article page (recipes).

    <recipe-page>  wraps sections/main-article.liquid.

  Servings
    [data-servings-input]   number input inside <quantity-input> (theme.js drives the buttons)
    [data-quantity]         ingredient quantity as typed in the metafield, with data-unit. Numbers
                            ("2", "0,5", "1/2") are scaled; anything else ("1–2", "bir tutam") stays
                            as typed. Whole numbers print as they are, others with one decimal at
                            most in the number format of the page language.
    [data-servings-text]    "1 kişi" in the facts; data-one / data-other hold the wording
    data-base-servings      servings the quantities are written for (on the element itself)

  Cart
    [data-add-all]          comma separated variant ids, added together through LM.cart.add

  Share
    [data-share]            wrapper with data-url and data-title
    [data-share-copy]       copies the link; data-label / data-label-done swap for a moment
    [data-share-native]     opens the share sheet of the device; stays hidden where there is none

  Layout
    --sticky-top            set to 0px when the header is not sticky, so the media column of the
                            article sticks to the top of the window instead of under the header
*/
(() => {
  if (customElements.get('recipe-page')) return;

  const LM = window.LM || {};

  const parseAmount = (text) => {
    const value = String(text || '').trim();
    if (/^\d+([.,]\d+)?$/.test(value)) return parseFloat(value.replace(',', '.'));
    const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(value);
    if (fraction && Number(fraction[2]) !== 0) return Number(fraction[1]) / Number(fraction[2]);
    return null;
  };

  class RecipePage extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.base = Math.max(1, parseInt(this.dataset.baseServings, 10) || 1);
      this.locale = document.documentElement.lang || 'tr-TR';
      this.input = this.querySelector('[data-servings-input]');

      if (this.input) {
        this.input.addEventListener('change', () => this.setServings(this.input.value));
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') this.setServings(this.input.value);
        });
      }

      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const addAll = event.target.closest('[data-add-all]');
        if (addAll) return this.addAll(addAll);
        const copy = event.target.closest('[data-share-copy]');
        if (copy) return this.copyLink(copy);
        const native = event.target.closest('[data-share-native]');
        if (native) return this.share(native);
        return undefined;
      });

      if (typeof navigator.share === 'function') {
        this.querySelectorAll('[data-share-native]').forEach((button) => {
          button.hidden = false;
        });
      }

      this.syncSticky = this.syncSticky.bind(this);
      this.syncSticky();
      window.addEventListener('resize', this.syncSticky);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.syncSticky);
      window.clearTimeout(this.copyTimer);
    }

    /* ---------- Servings ---------- */
    format(amount) {
      const rounded = Math.round(amount * 10) / 10;
      if (Number.isInteger(rounded)) return String(rounded);
      try {
        return rounded.toLocaleString(this.locale, { maximumFractionDigits: 1 });
      } catch (error) {
        return rounded.toLocaleString('tr-TR', { maximumFractionDigits: 1 });
      }
    }

    setServings(value) {
      const min = parseInt(this.input.min, 10) || 1;
      const max = parseInt(this.input.max, 10) || 6;
      const servings = Math.min(max, Math.max(min, parseInt(value, 10) || this.base));
      if (this.input.value !== String(servings)) this.input.value = String(servings);

      this.querySelectorAll('[data-quantity]').forEach((el) => {
        const amount = parseAmount(el.dataset.quantity);
        if (amount === null) return;
        const unit = el.dataset.unit || '';
        el.textContent = `${this.format((amount * servings) / this.base)} ${unit}`.trim();
      });

      this.querySelectorAll('[data-servings-text]').forEach((el) => {
        const template = servings === 1 ? el.dataset.one : el.dataset.other;
        if (template) el.textContent = template.replace('[count]', String(servings));
      });
    }

    /* ---------- Cart ---------- */
    async addAll(button) {
      if (button.classList.contains('is-loading') || !LM.cart) return;
      const items = (button.dataset.addAll || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1 }));
      if (!items.length) return;
      this.showError(button, '');
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      try {
        await LM.cart.add(items, { opener: button });
      } catch (error) {
        this.showError(button, (error && error.message) || (LM.strings && LM.strings.cartError) || '');
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }

    showError(button, message) {
      let el = button.nextElementSibling;
      if (!el || !el.hasAttribute('data-add-all-error')) {
        if (!message) return;
        el = document.createElement('p');
        el.className = 'field__error';
        el.setAttribute('data-add-all-error', '');
        el.setAttribute('role', 'alert');
        button.after(el);
      }
      el.textContent = message;
      el.hidden = !message;
    }

    /* ---------- Share ---------- */
    shareData(control) {
      const wrapper = control.closest('[data-share]');
      return {
        url: (wrapper && wrapper.dataset.url) || window.location.href,
        title: (wrapper && wrapper.dataset.title) || document.title,
      };
    }

    async copyLink(button) {
      const { url } = this.shareData(button);
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied) {
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(field);
        field.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        field.remove();
        button.focus({ preventScroll: true });
      }
      if (!copied) return;
      const label = button.querySelector('[data-share-copy-label]');
      if (label) label.textContent = button.dataset.labelDone || label.textContent;
      button.classList.add('is-active');
      if (LM.announce && button.dataset.labelDone) LM.announce(button.dataset.labelDone);
      window.clearTimeout(this.copyTimer);
      this.copyTimer = window.setTimeout(() => {
        if (label) label.textContent = button.dataset.label || label.textContent;
        button.classList.remove('is-active');
      }, 2000);
    }

    share(button) {
      const data = this.shareData(button);
      // Closing the share sheet rejects the promise; that is not an error.
      navigator.share(data).catch(() => {});
    }

    /* ---------- Layout ---------- */
    syncSticky() {
      const header = document.querySelector('[data-site-header]');
      const wrapper = header ? header.closest('.shopify-section') || header : null;
      const sticky = !!wrapper && window.getComputedStyle(wrapper).position === 'sticky';
      if (sticky) this.style.removeProperty('--sticky-top');
      else this.style.setProperty('--sticky-top', '0px');
    }
  }

  customElements.define('recipe-page', RecipePage);
})();
